
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import {
  InsightModel,
  IInsightRequest,
  IIndividualInsightRequest,
  IIndividualInsightResponse,
  InsightType,
  InsightPriority,
  IInsightData,
  InsightAction,
} from '@/models/insight';
import { MeasureModel } from '@/models/measure';
import { DimensionModel } from '@/models/dimension';
import InsightCard from './InsightCard.vue';
import QuestionCard from './QuestionCard.vue';
import QuestionSuggestionCard from './QuestionSuggestionCard.vue';
import SuggestionLoader from './SuggestionLoader.vue';
import RelatedQuestionsBar from './RelatedQuestionsBar.vue';

// Progressive loading interfaces
interface QuestionState {
  isLoading: boolean;
  isComplete: boolean;
  hasError: boolean;
  result?: IIndividualInsightResponse['data'];
  error?: string;
}

// NEW: Interface for user questions
interface UserQuestion {
  id: string;
  question: string;
  instruction: string;
  priority: 'high' | 'medium' | 'low';
  category: { name: string; code: string };
  icon: string;
  createdAt: Date;
  // Related suggestions from API response
  relatedSuggestions?: any[];
  suggestionsMetadata?: any;
  // NEW: Inline related questions
  inlineRelatedQuestions?: any[];
}

// NEW: Interface for predefined questions with inline support
interface PredefinedQuestion {
  id: string;
  question: string;
  instruction: string;
  icon: string;
  priority: 'high' | 'medium' | 'low';
  // Response tracking properties
  isProcessed?: boolean;
  relatedSuggestions?: any[];
  suggestionsMetadata?: any;
  // NEW: Inline related questions
  inlineRelatedQuestions?: any[];
}

@Component({
  components: {
    InsightCard,
    QuestionCard,
    QuestionSuggestionCard,
    SuggestionLoader,
    RelatedQuestionsBar,
  },
})
export default class InsightPanel extends Vue {

  get hasInsights(): boolean {
    return this.insight.hasInsights();
  }

  get hasError(): boolean {
    return this.insight.hasError();
  }

  get isMeasureSupported(): boolean {
    return InsightModel.isMeasureSupported(this.measure.code);
  }

  // NEW: Computed property for report context (required by QuestionCard) - simplified
  get reportContext(): any {
    return {
      measure: this.measure,
      dimension: this.dimension,
      filterTimerange: this.filterTimerange,
      reportColumns: this.reportColumns,
    };
  }

  // =============================================================================
  // 🚨 NEW: EXPLICIT RESPONSE TYPE SUPPORT COMPUTED PROPERTIES
  // =============================================================================

  get isDevelopmentMode(): boolean {
    return process.env.NODE_ENV === 'development';
  }

  get hasExplicitResponseTypeInsights(): boolean {
    return this.validInsights.some((insight) => !!insight.response_type);
  }

  get explicitResponseTypeInsights(): IInsightData[] {
    return this.validInsights.filter((insight) => !!insight.response_type);
  }

  get typeMismatchCount(): number {
    return this.validInsights.filter((insight) => !!insight.type_mismatch).length;
  }

  get validInsights() {
    // Filter out failed insights for display
    return this.insight.insights.filter((insight) => !this.isInsightError(insight));
  }

  get hasValidInsights(): boolean {
    return this.validInsights.length > 0;
  }

  // NEW: Progressive loading computed properties
  get hasAnyProgressiveResults(): boolean {
    return false; // No longer tracking individual question states
  }

  get completedQuestionsCount(): number {
    return 0; // No longer tracking individual question states
  }

  get totalQuestionsCount(): number {
    return this.predefinedQuestions.length;
  }

  get totalExecutionTime(): number {
    return 0; // No longer tracking individual question states
  }

  // NEW: Group questions by category for organized display
  get questionsByCategory(): Array<{
    category: {code: string, name: string, count: number, icon: string},
    questions: Array<{id: string, question: string, instruction: string, icon: string, priority: 'high' | 'medium' | 'low'}>,
  }> {
    if (!this.questionCategories.length || !this.predefinedQuestions.length) {
      return [];
    }

    const grouped = this.questionCategories.map((category) => {
      // Find questions that belong to this category
      const categoryQuestions = this.predefinedQuestions.filter((question) => {
        // Match by category code if available in question data
        // For now, we'll use the question.id to match with category.questions array
        return category.questions.includes(question.id);
      });

      return {
        category: {
          ...category,
          icon: this.getCategoryIcon(category.code),
        },
        questions: categoryQuestions,
      };
    }).filter((group) => group.questions.length > 0); // Only show categories with questions

    return grouped;
  }

  // NEW: Direct question examples from predefined questions
  get directQuestionExamples(): string[] {
    // Use the first 3 predefined questions as suggestions
    return this.predefinedQuestions
      .slice(0, 3)
      .map((question) => question.question)
      .filter((question) => question && question.length > 0);
  }
  // Props from Report.ts (simplified)
  @Prop({ default: () => new MeasureModel() })
  public measure!: MeasureModel;

  @Prop({ default: () => new DimensionModel() })
  public dimension!: DimensionModel;

  @Prop({ default: () => [] })
  public filterTimerange!: string[];

  @Prop({ default: () => [] })
  public reportColumns!: any[];

  // Control props
  @Prop({ default: false })
  public autoLoad!: boolean;
  @Prop({ default: false })
  public enableQuestionPin!: boolean;

  // Component state
  public insight: InsightModel = new InsightModel();
  public isLoading: boolean = false;

  // NEW: Progressive loading state
  public insightMode: 'progressive' | 'bulk' = 'progressive';
  public customQuestion: string = '';
  public isProcessingCustomQuestion: boolean = false;
  // NEW: Enhanced predefined questions with response tracking
  public predefinedQuestions: PredefinedQuestion[] = [];

  // NEW: User question management
  public newUserQuestion: string = '';
  public userQuestions: UserQuestion[] = [];
  public isAddingUserQuestion: boolean = false;

  // NEW: Dynamic question loading state
  public isLoadingQuestions: boolean = false;
  public questionCategories: Array<{code: string, name: string, count: number, questions: string[]}> = [];
  public questionsVersion: string = '';

  // NEW: Suggestion data from API responses (no separate API calls)
  public fallbackSuggestions: any[] = [];
  public fallbackSuggestionsMetadata: any = null;
  public fallbackSuggestionsInfo: any = null;
  public directSuggestions: any[] = [];
  public directSuggestionsMetadata: any = null;

  // NEW: Explicit Response Type Debug Support


  // Make InsightModel available to template
  public InsightModel = InsightModel;

  // NEW: User question management
  private nextQuestionId: number = 1;

  // Question suggestion defaults (always enabled)
  private readonly DEFAULT_SUGGESTION_COUNT: number = 5; // For big fallback suggestions
  private readonly RELATED_QUESTIONS_COUNT: number = 3;  // For individual question suggestions

  // NEW: Event handlers for QuestionCard components
  public handleQuestionProcessed(event: { questionId: string; result: any }): void {
    console.log('Question processed:', event.questionId, event.result);

    console.log('userQuestions:', this.userQuestions);
    // Check if it's a user question
    const userQuestion = this.userQuestions.find((q) => q.id === event.questionId);
    if (userQuestion) {
      console.log('✅ User question processed successfully:', userQuestion.question);
      console.log('🔍 Checking for suggestions in successful response...');
      
      // Check multiple possible paths where suggestions might be stored
      let suggestions = null;
      let suggestionsMetadata = null;
      
      if (event.result) {
        console.log('📊 Available response keys:', Object.keys(event.result));
        
        // Path 1: Direct access (event.result.suggested_questions)
        if (event.result.suggested_questions) {
          suggestions = event.result.suggested_questions;
          suggestionsMetadata = event.result.suggestion_metadata;
          console.log('📝 Found suggestions at: event.result.suggested_questions');
        }
        // Path 2: Nested in data (event.result.data.suggested_questions)
        else if (event.result.data && event.result.data.suggested_questions) {
          suggestions = event.result.data.suggested_questions;
          suggestionsMetadata = event.result.data.suggestion_metadata;
          console.log('📝 Found suggestions at: event.result.data.suggested_questions');
        }
        // Path 3: Check if there's insight data with suggestions
        else if (event.result.insight && event.result.insight.suggested_questions) {
          suggestions = event.result.insight.suggested_questions;
          suggestionsMetadata = event.result.insight.suggestion_metadata;
          console.log('📝 Found suggestions at: event.result.insight.suggested_questions');
        }
        else {
          console.log('⚠️ No suggestions found in successful response');
          console.log('🔍 Full response structure:', JSON.stringify(event.result, null, 2));
        }
      }
      
      if (suggestions && suggestions.length > 0) {
        // Use Vue.set to ensure reactivity for dynamically added properties
        this.$set(userQuestion, 'relatedSuggestions', suggestions);
        this.$set(userQuestion, 'suggestionsMetadata', suggestionsMetadata || null);
        console.log(`📝 SUCCESS: Captured ${suggestions.length} related suggestions for: ${userQuestion.question}`);
        
        // Force Vue to recognize the change
        this.$forceUpdate();
      } else {
        console.log('⚠️ No valid suggestions found for successful question');
      }
      return;
    }

    // NEW: Check if it's a predefined question
    const predefinedQuestion = this.predefinedQuestions.find((q) => q.id === event.questionId);
    if (predefinedQuestion) {
      console.log('✅ Predefined question processed successfully:', predefinedQuestion.question);
      console.log('🔍 Checking for suggestions in predefined question response...');
      
      // Check multiple possible paths where suggestions might be stored
      let suggestions = null;
      let suggestionsMetadata = null;
      
      if (event.result) {
        console.log('📊 Available response keys:', Object.keys(event.result));
        
        // Path 1: Direct access (event.result.suggested_questions)
        if (event.result.suggested_questions) {
          suggestions = event.result.suggested_questions;
          suggestionsMetadata = event.result.suggestion_metadata;
          console.log('📝 Found suggestions at: event.result.suggested_questions');
        }
        // Path 2: Nested in data (event.result.data.suggested_questions)
        else if (event.result.data && event.result.data.suggested_questions) {
          suggestions = event.result.data.suggested_questions;
          suggestionsMetadata = event.result.data.suggestion_metadata;
          console.log('📝 Found suggestions at: event.result.data.suggested_questions');
        }
        // Path 3: Check if there's insight data with suggestions
        else if (event.result.insight && event.result.insight.suggested_questions) {
          suggestions = event.result.insight.suggested_questions;
          suggestionsMetadata = event.result.insight.suggestion_metadata;
          console.log('📝 Found suggestions at: event.result.insight.suggested_questions');
        }
        else {
          console.log('⚠️ No suggestions found in predefined question response');
        }
      }
      
      // Mark question as processed and capture suggestions
      this.$set(predefinedQuestion, 'isProcessed', true);
      
      if (suggestions && suggestions.length > 0) {
        this.$set(predefinedQuestion, 'relatedSuggestions', suggestions);
        this.$set(predefinedQuestion, 'suggestionsMetadata', suggestionsMetadata || null);
        console.log(`📝 SUCCESS: Captured ${suggestions.length} related suggestions for predefined question: ${predefinedQuestion.question}`);
      } else {
        console.log('⚠️ No valid suggestions found for predefined question');
      }
      
      // Force Vue to recognize the change
      this.$forceUpdate();
    }
  }

  public handleQuestionFailed(event: { questionId: string; error: string; result?: any }): void {
    console.error('Question failed:', event.questionId, event.error);

    // Check if it's a user question
    const userQuestion = this.userQuestions.find((q) => q.id === event.questionId);
    if (userQuestion) {
      console.error('❌ User question failed:', userQuestion.question, event.error);
      
      // Still try to capture suggestions from failed response (if API returns them)
      // Check multiple possible paths where suggestions might be stored
      let suggestions = null;
      let suggestionsMetadata = null;
      
      if (event.result) {
        // Path 1: Direct access (event.result.suggested_questions)
        if (event.result.suggested_questions) {
          suggestions = event.result.suggested_questions;
          suggestionsMetadata = event.result.suggestion_metadata;

        }
        // Path 2: Nested in data (event.result.data.suggested_questions)
        else if (event.result.data && event.result.data.suggested_questions) {
          suggestions = event.result.data.suggested_questions;
          suggestionsMetadata = event.result.data.suggestion_metadata;

        }
        // Path 3: Axios error response structure (event.result.data nested further)
        else if (event.result.response && event.result.response.data && event.result.response.data.suggested_questions) {
          suggestions = event.result.response.data.suggested_questions;
          suggestionsMetadata = event.result.response.data.suggestion_metadata;
          console.log('📝 Found suggestions at: event.result.response.data.suggested_questions');
        }
      }
      
      if (suggestions && suggestions.length > 0) {
        this.$set(userQuestion, 'relatedSuggestions', suggestions);
        this.$set(userQuestion, 'suggestionsMetadata', suggestionsMetadata || null);
        console.log(`📝 Captured ${suggestions.length} related suggestions from failed question: ${userQuestion.question}`);
      } else {
        console.log('⚠️ No suggestions found in failed response. Available keys:', Object.keys(event.result || {}));
      }
      return;
    }

    // NEW: Check if it's a predefined question
    const predefinedQuestion = this.predefinedQuestions.find((q) => q.id === event.questionId);
    if (predefinedQuestion) {
      console.error('❌ Predefined question failed:', predefinedQuestion.question, event.error);
      
      // Still try to capture suggestions from failed response (if API returns them)
      let suggestions = null;
      let suggestionsMetadata = null;
      
      if (event.result) {
        // Path 1: Direct access (event.result.suggested_questions)
        if (event.result.suggested_questions) {
          suggestions = event.result.suggested_questions;
          suggestionsMetadata = event.result.suggestion_metadata;
        }
        // Path 2: Nested in data (event.result.data.suggested_questions)
        else if (event.result.data && event.result.data.suggested_questions) {
          suggestions = event.result.data.suggested_questions;
          suggestionsMetadata = event.result.data.suggestion_metadata;
        }
        // Path 3: Axios error response structure (event.result.data nested further)
        else if (event.result.response && event.result.response.data && event.result.response.data.suggested_questions) {
          suggestions = event.result.response.data.suggested_questions;
          suggestionsMetadata = event.result.response.data.suggestion_metadata;
          console.log('📝 Found suggestions at: event.result.response.data.suggested_questions');
        }
      }
      
      // Mark question as processed (even if failed) and capture suggestions
      this.$set(predefinedQuestion, 'isProcessed', true);
      
      if (suggestions && suggestions.length > 0) {
        this.$set(predefinedQuestion, 'relatedSuggestions', suggestions);
        this.$set(predefinedQuestion, 'suggestionsMetadata', suggestionsMetadata || null);
        console.log(`📝 Captured ${suggestions.length} related suggestions from failed predefined question: ${predefinedQuestion.question}`);
      } else {
        console.log('⚠️ No suggestions found in failed predefined question response');
      }
      
      // Force Vue to recognize the change
      this.$forceUpdate();
    }
  }

  public handleUserQuestionDeleted(event: { questionId: string }): void {
    const questionIndex = this.userQuestions.findIndex((q) => q.id === event.questionId);
    if (questionIndex !== -1) {
      const deletedQuestion = this.userQuestions[questionIndex];
      this.userQuestions.splice(questionIndex, 1);
      console.log('🗑️ User question deleted:', deletedQuestion.question);
    }
  }



  public handleDirectSuggestionSelected(event: { suggestion: any; question: string; index: number }): void {
    console.log('🎯 Direct suggestion selected:', event.suggestion.question);
    // Use short question for display, instruction for API
    const questionText = event.suggestion.question || event.question;
    const instructionText = event.suggestion.instruction || event.suggestion.question || event.question;
    console.log('📝 Display question:', questionText);
    console.log('📝 API instruction:', instructionText);
    
    // Add the suggestion as a new user question with proper separation
    this.addUserQuestionFromSuggestion(questionText, instructionText);
  }

  public handleFallbackSuggestionSelected(event: { suggestion: any; question: string; index: number }): void {
    console.log('🎯 Fallback suggestion selected:', event.suggestion.question);
    // Use short question for display, instruction for API
    const questionText = event.suggestion.question || event.question;
    const instructionText = event.suggestion.instruction || event.suggestion.question || event.question;
    console.log('📝 Display question:', questionText);
    console.log('📝 API instruction:', instructionText);
    
    // Add the suggestion as a new user question with proper separation
    this.addUserQuestionFromSuggestion(questionText, instructionText);
  }

  public handleRelatedQuestionSelected(event: { suggestion: any; question: string; parentQuestionId?: string }): void {
    console.log('🎯 Related question selected:', event.suggestion.question);
    // Use short question for display, instruction for API
    const questionText = event.suggestion.question || event.question;
    const instructionText = event.suggestion.instruction || event.suggestion.question || event.question;
    console.log('📝 Display question:', questionText);
    console.log('📝 API instruction:', instructionText);
    
    // Check if we have a parent question ID for inline placement
    if (event.parentQuestionId) {
      console.log('🔗 Adding inline related question for parent:', event.parentQuestionId);
      const transformedQuestion = this.transformSuggestionToQuestion(event.suggestion);
      
      if (transformedQuestion) {
        this.addInlineRelatedQuestion(event.parentQuestionId, transformedQuestion);
      } else {
        console.warn('⚠️ Failed to transform suggestion, falling back to user question');
        this.addUserQuestionFromSuggestion(questionText, instructionText);
      }
    } else {
      // Fallback to adding as separate user question
      this.addUserQuestionFromSuggestion(questionText, instructionText);
    }
  }

  public async mounted() {
    // Load questions dynamically from backend instead of hardcoded
    if (this.isMeasureSupported) {
      await this.loadQuestions();
    }

    if (this.autoLoad && this.isMeasureSupported) {
      if (this.insightMode === 'bulk') {
        await this.loadInsights();
      }
    }
  }
@Watch('filterTimerange')
  async onTimeRangeChanged() {
    if (this.isMeasureSupported && this.hasInsights) {
      // Time range changed, reload insights with cache clearing
      await this.loadInsights();
    }
  }
@Watch('dimension')
  async onDimensionChanged() {
    console.log('InsightPanel: Dimension changed to:', this.dimension?.code);
    await this.handleMeasureOrDimensionChange('dimension');
  }

@Watch('measure')
  async onMeasureChanged() {
    console.log('InsightPanel: Measure changed to:', this.measure?.code);
    await this.handleMeasureOrDimensionChange('measure');
  }

  // NEW: Unified handler for measure or dimension changes to prevent duplicate API calls
  private async handleMeasureOrDimensionChange(changeType: 'measure' | 'dimension'): Promise<void> {
    if (!this.isMeasureSupported) {
      // Clear everything if measure is not supported
      this.clearAllInsights();
      this.predefinedQuestions = [];
      this.questionCategories = [];
      return;
    }

    // For measure changes, clear insights first
    if (changeType === 'measure') {
      this.clearAllInsights();
    }

    // Load questions (with duplicate call protection)
    await this.loadQuestions();

    // For dimension changes, reload insights if they exist
    if (changeType === 'dimension' && this.hasInsights) {
      await this.loadInsights();
    }
  }

  // NEW: Load questions dynamically from backend
  public async loadQuestions(): Promise<void> {
    if (!this.isMeasureSupported) {
      console.log('InsightPanel: Measure not supported for insights:', this.measure.code);
      return;
    }

    // Prevent duplicate API calls when both measure and dimension change simultaneously
    if (this.isLoadingQuestions) {
      console.log('📊 loadQuestions already in progress, skipping duplicate call');
      return;
    }

    try {
      this.isLoadingQuestions = true;

      console.log(`📊 Loading questions for measure: ${this.measure.code}, dimension: ${this.dimension.code}`);

      // Use the updated fetchQuestions method that handles optional dimension
      const response = await InsightModel.fetchQuestions(
        this.measure.code, 
        this.dimension.code || undefined
      );

      if (response.success && response.data) {
        // Transform questions for UI display
        this.predefinedQuestions = this.transformQuestionsForUI(response.data.questions);
        console.log('InsightPanel: Predefined questions:', this.predefinedQuestions);
        this.questionCategories = response.data.categories;
        this.questionsVersion = response.data.version;

        // Capture fallback suggestions from API response (when no predefined questions)
        const responseData = response.data as any;
        if (responseData.fallback_suggestions) {
          this.fallbackSuggestions = responseData.fallback_suggestions;
          this.fallbackSuggestionsMetadata = responseData.suggestion_metadata || null;
          this.fallbackSuggestionsInfo = responseData.fallback_info || null;
          console.log(`📝 Captured ${this.fallbackSuggestions.length} fallback suggestions from API`);
        } else {
          this.fallbackSuggestions = [];
          this.fallbackSuggestionsMetadata = null;
          this.fallbackSuggestionsInfo = null;
        }

        console.log(`✅ Loaded ${response.data.total_questions} questions in ${response.data.categories.length} categories`);
      } else {
        console.error('Failed to load questions:', response.message);
        // Fallback to empty questions and suggestions
        this.predefinedQuestions = [];
        this.questionCategories = [];
        this.fallbackSuggestions = [];
        this.fallbackSuggestionsMetadata = null;
        this.fallbackSuggestionsInfo = null;
      }

    } catch (error) {
      console.error('Error loading questions:', error);
      // Fallback to empty questions
      this.predefinedQuestions = [];
      this.questionCategories = [];
    } finally {
      this.isLoadingQuestions = false;
    }
  }

  public async loadInsights(): Promise<void> {
    if (!this.isMeasureSupported) {
      console.log('InsightPanel: Measure not supported for insights:', this.measure.code);
      return;
    }

    try {
      this.isLoading = true;

      // Extract parameters from report props (simplified)
      const requestParams: IInsightRequest = InsightModel.extractParametersFromReport({
        measure: this.measure,
        dimension: this.dimension,
        filterTimerange: this.filterTimerange,
        reportColumns: this.reportColumns,
        
      });

      console.log('InsightPanel: Loading insights with params:', requestParams);

      await this.insight.fetch(requestParams);

    } catch (error) {
      console.error('InsightPanel: Error loading insights:', error);
    } finally {
      this.isLoading = false;
    }
  }

  public async refreshInsights(): Promise<void> {
    await this.loadInsights();
  }

  // Helper methods for styling
  public getInsightIcon(type: InsightType): string {
    const iconMap: Record<InsightType, string> = {
      ALERT: 'mdi-alert',
      RECOMMENDATION: 'mdi-lightbulb',
      METRIC: 'mdi-chart-line',
      TREND: 'mdi-trending-up',
      COMPARISON: 'mdi-compare',
      SUMMARY: 'mdi-format-list-bulleted',
    };
    return iconMap[type] || 'mdi-information';
  }

  public getInsightColor(type: InsightType, priority: InsightPriority): string {
    if (priority === 'HIGH') { return 'error'; }
    if (priority === 'MEDIUM') { return 'warning'; }

    const colorMap: Record<InsightType, string> = {
      ALERT: 'error',
      RECOMMENDATION: 'orange',
      METRIC: 'primary',
      TREND: 'success',
      COMPARISON: 'purple',
      SUMMARY: 'grey',
    };
    return colorMap[type] || 'primary';
  }

  public getPriorityColor(priority: InsightPriority): string {
    const colorMap: Record<InsightPriority, string> = {
      HIGH: 'error',
      MEDIUM: 'warning',
      LOW: 'success',
    };
    return colorMap[priority] || 'grey';
  }

  public getTrendIcon(trend: string): string {
    const iconMap: Record<string, string> = {
      up: 'mdi-trending-up',
      down: 'mdi-trending-down',
      stable: 'mdi-trending-neutral',
    };
    return iconMap[trend] || 'mdi-minus';
  }

  public getTrendColor(trend: string): string {
    const colorMap: Record<string, string> = {
      up: 'success',
      down: 'error',
      stable: 'warning',
    };
    return colorMap[trend] || 'grey';
  }

  public formatInsightValue(value: number): string {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toFixed(2);
  }

  public getInsightMessage(insightItem: any): string {
    // NEW: Check for structured format 'insight' field first, then fallback to legacy fields
    return insightItem.insight || insightItem.message || insightItem.answer || '';
  }

  public isInsightError(insightItem: any): boolean {
    const message = this.getInsightMessage(insightItem);
    // Check if the AI returned an error message
    return message.includes('Unfortunately, I was not able to answer') ||
           message.includes('error') ||
           message.length === 0;
  }

  public getNoInsightsMessage(): string {
    if (this.insight.error) {
      return 'There was an error generating insights. Please try refreshing or contact support.';
    }

    // Check if all insights failed
    const allInsights = this.insight.insights || [];
    const failedInsights = allInsights.filter((insight) => this.isInsightError(insight));

    if (allInsights.length > 0 && failedInsights.length === allInsights.length) {
      return 'AI processing temporarily unavailable. Insights could not be generated at this time.';
    }

    return 'Insights are not available for this report configuration. Try changing the date range or report filters.';
  }

  // NEW: Dynamic placeholder text
  public getPlaceholderText(): string {
    if (!this.isMeasureSupported) {
      return 'AI insights not available for this report type';
    }
    if (this.isAddingUserQuestion) {
      return 'Processing your question...';
    }
    const examples = this.directQuestionExamples;
    return examples.length > 0 ? examples[0] : 'Ask any question about your data...';
  }

  // NEW: Dynamic hint text
  public getHintText(): string {
    if (this.isAddingUserQuestion) {
      return 'Your question is being processed...';
    }
    return 'Press Enter to ask, Esc to clear';
  }

  // NEW: Progressive loading methods
  public onModeChange(): void {
    console.log('InsightPanel: Mode changed to', this.insightMode);

    if (this.insightMode === 'bulk' && this.autoLoad && this.isMeasureSupported) {
      this.loadInsights();
    }
  }

  public async askCustomQuestion(): Promise<void> {
    if (!this.customQuestion.trim() || !this.isMeasureSupported) { return; }

    try {
      this.isProcessingCustomQuestion = true;

      const requestParams: IIndividualInsightRequest = InsightModel.createIndividualQuestionRequest(
        this.customQuestion.trim(),
        {
          measure: this.measure,
          dimension: this.dimension,
          filterTimerange: this.filterTimerange,
          reportColumns: this.reportColumns,
        },
      );

      const response: IIndividualInsightResponse = await this.insight.askQuestion(requestParams);

      if (response.success && response.data) {
        console.log('Custom question successful:', response.data);
        // You could add the result to a separate custom results array if needed
      } else {
        console.error('Custom question failed:', response.message);
      }

      this.customQuestion = '';

    } catch (error) {
      console.error('Error asking custom question:', error);
    } finally {
      this.isProcessingCustomQuestion = false;
    }
  }

  // NEW: User question management methods
  public selectSuggestion(example: string): void {
    // For predefined example suggestions, use the example text directly
    // since these come from predefined questions that might not have separate instructions
    this.newUserQuestion = example;
    this.$nextTick(() => {
      // Focus the input field after the DOM updates
      const inputRef = this.$refs.questionInput as any;
      if (inputRef && inputRef.focus) {
        inputRef.focus();
      } else if (inputRef && inputRef.$el) {
        // For Vuetify components, we might need to access the actual input element
        const inputElement = inputRef.$el.querySelector('input');
        if (inputElement && inputElement.focus) {
          inputElement.focus();
        }
      }
    });
  }

  public async addUserQuestion(): Promise<void> {
    const MAX_USER_QUESTIONS = 30;

    if (!this.newUserQuestion.trim() || !this.isMeasureSupported) { return; }

    // Auto-remove oldest question when hitting the limit
    if (this.userQuestions.length >= MAX_USER_QUESTIONS) {
      // Remove the oldest question (last in array since we use unshift)
      const removedQuestion = this.userQuestions.pop();
      console.log(`🗑️ Auto-removed oldest question to make room: "${removedQuestion?.question}"`);
    }

    try {
      this.isAddingUserQuestion = true;

      const userQuestion: UserQuestion = {
        id: `user_${this.nextQuestionId++}`,
        question: this.newUserQuestion.trim(),
        instruction: this.newUserQuestion.trim(), // Same as question for direct questions
        priority: 'high',
        category: { name: 'Direct Question', code: 'user_question' },
        icon: '💬',
        createdAt: new Date(),
      };

      // Add to beginning of array (most recent first)
      this.userQuestions.unshift(userQuestion);

      // Clear input
      this.newUserQuestion = '';

      console.log('✅ User question added:', userQuestion.question);

    } catch (error) {
      console.error('❌ Failed to add user question:', error);
    } finally {
      this.isAddingUserQuestion = false;
    }
  }

  public async addUserQuestionFromSuggestion(questionText: string, instructionText: string): Promise<void> {
    const MAX_USER_QUESTIONS = 30;

    if (!questionText.trim() || !this.isMeasureSupported) { return; }

    // Auto-remove oldest question when hitting the limit
    if (this.userQuestions.length >= MAX_USER_QUESTIONS) {
      // Remove the oldest question (last in array since we use unshift)
      const removedQuestion = this.userQuestions.pop();
      console.log(`🗑️ Auto-removed oldest question to make room: "${removedQuestion?.question}"`);
    }

    try {
      this.isAddingUserQuestion = true;

      const userQuestion: UserQuestion = {
        id: `user_${this.nextQuestionId++}`,
        question: questionText.trim(),
        instruction: instructionText.trim(),
        priority: 'high',
        category: { name: 'Direct Question', code: 'user_question' },
        icon: '💬',
        createdAt: new Date(),
      };

      // Add to beginning of array (most recent first)
      this.userQuestions.unshift(userQuestion);

      // Clear input
      this.newUserQuestion = '';

      console.log('✅ User question added:', userQuestion.question);

    } catch (error) {
      console.error('❌ Failed to add user question from suggestion:', error);
    } finally {
      this.isAddingUserQuestion = false;
    }
  }

  public clearAllUserQuestions(): void {
    if (this.userQuestions.length === 0) { return; }

    if (confirm(`Delete all ${this.userQuestions.length} user questions?`)) {
      this.userQuestions = [];
      console.log('🗑️ All user questions cleared');
    }
  }



  public clearProgressiveResults(): void {
    this.customQuestion = '';
    console.log('Progressive results cleared');
  }

  // NEW: Clear all insights data (both progressive and bulk)
  public clearAllInsights(): void {
    // Clear progressive loading state
    this.customQuestion = '';
    this.isProcessingCustomQuestion = false;

    // Clear bulk insights
    this.insight = new InsightModel();
    this.isLoading = false;

    console.log('All insights data cleared');
  }

  // Helper method to map priority strings (lowercase) to colors
  public getPriorityColorFromString(priority: string): string {
    const priorityMap: { [key: string]: string } = {
      high: 'error',
      medium: 'warning',
      low: 'success',
    };
    return priorityMap[priority.toLowerCase()] || 'grey';
  }



  // =============================================================================
  // 🚨 NEW: INSIGHT ACTION HANDLERS FOR EXPLICIT RESPONSE TYPE SUPPORT
  // =============================================================================

  public handleInsightAction(action: InsightAction, insight: IInsightData): void {
    console.log('InsightPanel: Action clicked:', action, insight);

    // Emit to parent for global handling
    this.$emit('insight-action', action, insight);

    // Handle specific action types
    this.handleActionByType(action, insight);
  }

  public handleApplyFilter(filterParams: any): void {
    console.log('InsightPanel: Apply filter:', filterParams);
    this.$emit('apply-filter', filterParams);
  }

  public handleNavigate(url: string): void {
    console.log('InsightPanel: Navigate to:', url);

    // Use Vue Router for internal navigation
    if (url.startsWith('/')) {
      this.$router.push(url);
    } else {
      // External URL
      window.open(url, '_blank');
    }
  }

  public handleExportData(exportParams: any): void {
    console.log('InsightPanel: Export data:', exportParams);
    this.$emit('export-data', exportParams);
  }

  public handleQuestionPinned(event: { questionId: string }): void {
    console.log('Question pinned:', event.questionId);
    this.$emit('question-pinned', event);
  }

  // NEW: Transform suggestion to Question format
  public transformSuggestionToQuestion(suggestion: any): any {
    // Input validation
    if (!suggestion) {
      console.warn('⚠️ Invalid suggestion provided for transformation:', suggestion);
      return null;
    }
    
    if (!suggestion.question && !suggestion.instruction) {
      console.warn('⚠️ Suggestion missing both question and instruction:', suggestion);
      return null;
    }
    
    try {
      return {
        id: suggestion.id || `suggestion_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        question: suggestion.question || suggestion.instruction || 'AI Generated Question',
        instruction: suggestion.instruction || suggestion.question || 'AI Generated Question',
        priority: this.mapComplexityToPriority(suggestion.complexity),
        category: suggestion.category ? {
          name: suggestion.category.name || 'AI Generated',
          code: suggestion.category.code || 'ai_generated'
        } : {
          name: 'AI Generated',
          code: 'ai_generated'
        },
        icon: this.getIconForSuggestionType(suggestion),
        originalSuggestion: suggestion,
        suggestionType: 'ai_generated',
        createdAt: new Date(),
      };
    } catch (error) {
      console.error('❌ Error transforming suggestion to question:', error, suggestion);
      return null;
    }
  }

  // NEW: Add inline related question to parent
  public addInlineRelatedQuestion(parentQuestionId: string, relatedQuestion: any): void {
    // First check user questions
    const userQuestion = this.userQuestions.find(q => q.id === parentQuestionId);
    if (userQuestion) {
      if (!userQuestion.inlineRelatedQuestions) {
        this.$set(userQuestion, 'inlineRelatedQuestions', []);
      }
      userQuestion.inlineRelatedQuestions!.push(relatedQuestion);
      console.log(`✅ Added inline related question to user question: ${userQuestion.question}`);
      this.$forceUpdate();
      return;
    }

    // Then check predefined questions
    const predefinedQuestion = this.predefinedQuestions.find(q => q.id === parentQuestionId);
    if (predefinedQuestion) {
      if (!predefinedQuestion.inlineRelatedQuestions) {
        this.$set(predefinedQuestion, 'inlineRelatedQuestions', []);
      }
      predefinedQuestion.inlineRelatedQuestions!.push(relatedQuestion);
      console.log(`✅ Added inline related question to predefined question: ${predefinedQuestion.question}`);
      this.$forceUpdate();
      return;
    }

    console.warn('⚠️ Parent question not found for inline related question:', parentQuestionId);
    
    // Fallback: Add as regular user question if parent not found
    const fallbackQuestion: UserQuestion = {
      id: `fallback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      question: relatedQuestion.question || 'Related Question',
      instruction: relatedQuestion.instruction || relatedQuestion.question || 'Related Question',
      priority: relatedQuestion.priority || 'medium',
      category: relatedQuestion.category || { name: 'AI Generated', code: 'ai_generated' },
      icon: relatedQuestion.icon || '🤖',
      createdAt: new Date(),
    };
    
    // Add to beginning of user questions as fallback
    this.userQuestions.unshift(fallbackQuestion);
    console.log('🔄 Added as fallback user question:', fallbackQuestion.question);
  }

  // NEW: Helper methods for suggestion transformation
  private mapComplexityToPriority(complexity?: string): 'high' | 'medium' | 'low' {
    const complexityMap: { [key: string]: 'high' | 'medium' | 'low' } = {
      'high': 'high',
      'medium': 'medium', 
      'low': 'low',
      'complex': 'high',
      'simple': 'low'
    };
    return complexityMap[complexity?.toLowerCase() || 'medium'] || 'medium';
  }

  private getIconForSuggestionType(suggestion: any): string {
    // Map suggestion types to appropriate icons
    const typeIconMap: { [key: string]: string } = {
      'drill_down': '🔍',
      'comparison': '⚖️',
      'trend_analysis': '📈',
      'recommendation': '💡',
      'alert': '⚠️',
      'metric': '📊'
    };

    const categoryIconMap: { [key: string]: string } = {
      'diagnostic': '🔍',
      'actionable': '💡',
      'summary': '📋'
    };

    return suggestion.icon || 
           typeIconMap[suggestion.type] || 
           categoryIconMap[suggestion.category?.code] || 
           '🤖';
  }

  // Transform backend questions to UI format
  private transformQuestionsForUI(questions: any[]): PredefinedQuestion[] {
    return questions.map((q) => ({
      id: q.id,
      question: q.question || this.createQuestionDisplayText(q), // Use the short question field
      instruction: q.instruction || '', // Use the detailed instruction
      icon: this.getQuestionIcon(q),
      priority: this.mapPriorityToUI(q.priority),
      // Initialize response tracking properties
      isProcessed: false,
      relatedSuggestions: undefined,
      suggestionsMetadata: undefined,
      inlineRelatedQuestions: undefined,
    }));
  }

  private createQuestionDisplayText(question: any): string {
    // Create concise display text from instruction
    const instruction = question.instruction || '';

    // Extract the key question from the instruction
    if (instruction.includes('?')) {
      // Find the main question
      const questionParts = instruction.split('?');
      return questionParts[0].trim() + '?';
    }

    // Fallback: Use first sentence or truncate
    const firstSentence = instruction.split('.')[0];
    return firstSentence.length > 80 ? firstSentence.substring(0, 80) + '...' : firstSentence;
  }

  private getQuestionIcon(question: any): string {
    // Map question type/category to icons
    const typeIconMap: { [key: string]: string } = {
      ALERT: '⚠️',
      RECOMMENDATION: '💡',
      METRIC: '📊',
      SUMMARY: '📋',
      ANALYSIS: '🔍',
      COMPARISON: '⚖️',
    };

    const categoryIconMap: { [key: string]: string } = {
      high_level_summary: '📊',
      diagnostic_insights: '⚠️',
      actionable_insights: '🛒',
    };

    return categoryIconMap[question.category?.code] ||
           typeIconMap[question.type] ||
           '📈';
  }

  private mapPriorityToUI(priority: string): 'high' | 'medium' | 'low' {
    const priorityMap: { [key: string]: 'high' | 'medium' | 'low' } = {
      high: 'high',
      medium: 'medium',
      low: 'low',
    };
    return priorityMap[priority.toLowerCase()] || 'medium';
  }

  private getCategoryIcon(categoryCode: string): string {
    const categoryIconMap: { [key: string]: string } = {
      high_level_summary: '📊',
      diagnostic_insights: '⚠️',
      actionable_insights: '💡',
    };
    return categoryIconMap[categoryCode] || '📈';
  }

  private handleActionByType(action: InsightAction, insight: IInsightData): void {
    switch (action.type) {
      case 'filter':
        this.handleApplyFilter(action.params);
        break;
      case 'navigate':
        this.handleNavigate(action.url || '/');
        break;
      case 'export':
        this.handleExportData(action.params);
        break;
      case 'create':
        this.$emit('create-item', action.params);
        break;
      case 'update':
        this.$emit('update-item', action.params);
        break;
      default:
        console.log('InsightPanel: Unhandled action type:', action.type);
    }
  }


}
